import api from "@/api";
import type { ExternalEntityVisibilityFragment } from "@/graphql";
import { Module_Type } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import { useAuthStore } from "@/stores/authStore";
import { simpleError } from "@/stories/displays/SimpleError/SimpleError.vue";
import type { LoadingType, VisibleStateTypes } from "@/types";
import { computed, ref, type Ref } from "vue";

type DataPackage = {
  status: LoadingType;
  items: ExternalEntityVisibilityFragment[];
};

type Store = {
  item: {
    id?: number | string;
    externalContentVisibility?: VisibleStateTypes;
  };
  setOriginalHash: () => void;
};

const DEFAULT_DATA_PACKAGE: DataPackage = {
  status: "none",
  items: [],
};

const data = ref<Record<Module_Type, DataPackage>>({
  [Module_Type.Activity]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.Booking]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.Gallery]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.Groups]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.Library]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.Meal]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.Meeting]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.MyDay]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.News]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.Profiles]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.WhatsHappening]: { ...DEFAULT_DATA_PACKAGE },
  [Module_Type.WorkSchedule]: { ...DEFAULT_DATA_PACKAGE },
});

export function useEntityVisibility(moduleType: Module_Type) {
  const authStore = useAuthStore();
  const visibilities = computed(() => data.value[moduleType].items);

  const load = async () => {
    if (data.value[moduleType].status !== "none") {
      return;
    }

    data.value[moduleType].status = "loading";
    data.value[moduleType].items =
      await api.externalContent.getVisibilities(moduleType);
    data.value[moduleType].status = "loaded";
  };

  const setStatus = (entityId: string, status?: VisibleStateTypes) => {
    if (status === "visible") {
      setVisible(entityId);
    } else if (status === "hidden") {
      setHidden(entityId);
    }
  };

  const setVisible = (entityId: string) => {
    if (data.value[moduleType] === undefined) {
      return;
    }

    data.value[moduleType].items = data.value[moduleType].items.filter(
      (item) => item.entityId !== entityId,
    );
  };

  const setHidden = (entityId: string) => {
    if (data.value[moduleType] === undefined) {
      return;
    }

    data.value[moduleType].items.push({
      entityType: moduleType,
      entityId,
      institutionId: authStore.institutionId,
    });
  };

  const isVisible = (entityId?: number | string): VisibleStateTypes => {
    if (!entityId) {
      return "visible";
    }

    const isFound = data.value[moduleType].items.some(
      (item) => item.entityId === entityId.toString(),
    );

    if (isFound) {
      return "hidden";
    }

    return "visible";
  };

  const status = computed(() => data.value[moduleType].status);

  const loading = computed(() => {
    return status.value === "loading";
  });

  const isReady = () => isDataReady(status);

  const updateEntityVisibility = async (
    store: Store,
    loading: Ref<string | null>,
  ) => {
    if (!store.item.id) {
      return;
    }

    try {
      const entityId = store.item.id.toString();
      loading.value = "global.externalContent.saving_loading_message";
      await api.externalContent.setVisibility({
        entityId,
        entityType: moduleType,
        institutionId: authStore.institutionId,
        status: store.item.externalContentVisibility === "visible",
      });

      store.setOriginalHash();

      setStatus(entityId, store.item.externalContentVisibility);
    } catch (error) {
      simpleError("global.error_api");
    } finally {
      loading.value = null;
    }
  };

  return {
    loading,
    visibilities,
    load,
    setStatus,
    setVisible,
    setHidden,
    isReady,
    isVisible,
    updateEntityVisibility,
  };
}
